import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { PopoverOptionsComponent } from "src/components/popup/popover-options/popover-options.component";
import { AccountUpdate } from "src/model/class/general classes/account";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { UsersRole } from "src/model/enum/enumDigitalPlace";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iCredential } from "src/model/interface/icredential";
import { AccountService } from "src/service/account.service";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { SecuretyDataProtectionService } from "src/service/securety-data-protection.service";
import { SystemRouterService } from "src/service/system-router.service";
import { currentUserUpdateData } from "src/state/actions/authentication.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";

@Component({
    selector: "app-form-user-profile",
    templateUrl: "./form-user-profile.component.html",
    styleUrls: ["./form-user-profile.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormUserProfileComponent implements OnInit {
  private _logContext: string = "form-user-profile.component";
  private _formSubscriptions: Array<Subscription> = null;
  private _selectCurrentUserSub: Subscription = null;
  private _translateSubscription: Subscription = null;
  private _errorUnAuthorizeSubscription: Subscription = null;
  private _keyboardSubscriptions: Subscription = null;
  private _isPerformingLogin: boolean = false;
  private _errorCredentialLoginCount: number = 0;
  private _onPerformUnauthorizeAccess = new Subject<number>();
  public arrayRole: Array<UsersRole> = new Array<UsersRole>();
  private _userStore: CurrentSessionData = null;

  //#region Translate variable
  private _lblAdministrator: string = "";
  private _lblMaintaner: string = "";
  private _lblUser: string = "";
  private _lblDescriptionUser: string = "";
  private _lblDescriptionMaintaner: string = "";
  private _lblDescriptionAdministrator: string = "";
  private _lblpasswordMustMatch: string = "";
  //#endregion

  public formCreateAccount: UntypedFormGroup = null;
  public passwordInputType: string = "password";
  public passwordInputTypeConfirm: string = "password";
  public passwordIconType: string = "eye";
  public passwordIconTypeConfirm: string = "eye";
  public cssPlatformClass: string = "";
  public forcedActive: boolean = false;
  public isLengthEight: boolean = false;
  public isUpper: boolean = false;
  public isLower: boolean = false;
  public isSpecialChar: boolean = false;
  public isNumber: boolean = false;
  public isVisibleTooltip: boolean = false;
  @Output() onSaveData: EventEmitter<void> = new EventEmitter();

  constructor (
    private _fb: UntypedFormBuilder,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _routerService: SystemRouterService,
    private _systemRouterService: SystemRouterService,
    private _accountService: AccountService,
    private _loaderService: LoaderService,
    public popoverController: PopoverController,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _store: Store
  ) { }

  ngOnInit() {
    this.forcedActive = false;
    this.initialiseArrayRole();

    this.loadTranslation();
    //this.subscribeTranslationEvent();

    this.initializeCreateAccountForm();
    this.subscribeFormEvent();
  }

  ngOnDestroy() {
    this.unsubscribeFormEvent();

    this._translateSubscription?.unsubscribe();
    this._errorUnAuthorizeSubscription?.unsubscribe();
    this._keyboardSubscriptions?.unsubscribe();
  }

  //Show tooltip with requirements for password
  public async showPopover() {
    const popover = await this.popoverController.create({
      component: PopoverOptionsComponent,
      event: event,
      translucent: false,
      showBackdrop: false,
    });

    await popover.present();
  }

  /**
   * Set variabile to hide tooltip
   */
  public hideTooltip() {
    this.isVisibleTooltip = false;
  }

  /**
   * Set variabile to show tooltip
   */
  public showTooltip() {
    this.isVisibleTooltip = true;
  }

  /**
   * Load translated strings
   */
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.administrator",
        "dictionary.maintenance",
        "dictionary.condominium",
        "dictionary.descriptionRoleAdministrator",
        "dictionary.descriptionRoleMaintance",
        "dictionary.descriptionRoleCondominium",
        "dictionary.passwordMustMatch",
      ])
      .subscribe((translatedStrings) => {
        this._lblAdministrator = translatedStrings["dictionary.administrator"];
        this._lblMaintaner = translatedStrings["dictionary.maintenance"];
        this._lblUser = translatedStrings["dictionary.condominium"];
        this._lblDescriptionAdministrator =
          translatedStrings["dictionary.descriptionRoleAdministrator"];
        this._lblDescriptionMaintaner =
          translatedStrings["dictionary.descriptionRoleMaintance"];
        this._lblDescriptionUser =
          translatedStrings["dictionary.descriptionRoleCondominium"];
        this._lblpasswordMustMatch =
          translatedStrings["dictionary.passwordMustMatch"];
      });
  }

  //#endregion

  /**
   * Return to login page
   */
  public onClickGoBack() {
    this._systemRouterService.navigateToDigitalPlaceLogin();
  }

  /**
   * Get label to role by id
   * @param id
   * @returns
   */
  public translateRole(id) {
    let lblReturned: string = "";
    if (id == UsersRole.BUILDING_ADMINISTRATOR) {
      lblReturned = this._lblAdministrator;
    }
    if (id == UsersRole.MAINTANER) {
      lblReturned = this._lblMaintaner;
    }
    if (id == UsersRole.RESIDENT) {
      lblReturned = this._lblUser;
    }
    return lblReturned;
  }

  /**
   * Navigate to form to create and account
   */
  public onClickCreateAccount() {
    this._routerService.navigateToCreateAccount();
  }

  /**
   * Set role on ion-select
   */
  public onClickSelectItem(idRole) {
    this.formCreateAccount.get("role").setValue(idRole);
  }

  public isSelectedRole(idRole) {
    if (this.formCreateAccount.get("role").value == idRole) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Get the role of the page
   * @param idRole
   * @returns
   */
  public getRolePage(idRole) {
    let returnedString: string = "";
    if (idRole == UsersRole.BUILDING_ADMINISTRATOR) {
      returnedString = this._lblAdministrator;
    }
    if (idRole == UsersRole.MAINTANER) {
      returnedString = this._lblMaintaner;
    }
    if (idRole == UsersRole.RESIDENT) {
      returnedString = this._lblUser;
    }
    return returnedString;
  }

  /**
   * Get the description of the role
   * @param idRole
   * @returns
   */
  public getDescriptionRole(idRole) {
    let returnedDescription: string = "";
    if (idRole == UsersRole.BUILDING_ADMINISTRATOR) {
      returnedDescription = this._lblDescriptionAdministrator;
    }
    if (idRole == UsersRole.MAINTANER) {
      returnedDescription = this._lblDescriptionMaintaner;
    }
    if (idRole == UsersRole.RESIDENT) {
      returnedDescription = this._lblDescriptionUser;
    }
    return returnedDescription;
  }

  /**
   * Return icon by role
   */
  public returnIcon(idRole) {
    let returnedIcon: string = "";
    if (idRole == UsersRole.BUILDING_ADMINISTRATOR) {
      returnedIcon = "shield";
    }
    if (idRole == UsersRole.MAINTANER) {
      returnedIcon = "build";
    }
    if (idRole == UsersRole.RESIDENT) {
      returnedIcon = "business";
    }
    return returnedIcon;
  }

  /**
   * Change input type for password input
   */
  public onClickChangeInputTypePasswordUser() {
    if (this.passwordInputType === "password") {
      this.passwordInputType = "input";
      this.passwordIconType = "eye-off";
    } else {
      this.passwordInputType = "password";
      this.passwordIconType = "eye";
    }
  }

  /**
   * Handle callback event for request login
   *
   * @param objectLogin
   */
  public async onRequestLogin(objectLogin: iCredential): Promise<void> {
    if (!this._isPerformingLogin) {
    }
  }

  /** Change input type for password input
   */
  public onClickChangeInputTypePasswordUserConfirm() {
    if (this.passwordInputTypeConfirm === "password") {
      this.passwordInputTypeConfirm = "input";
      this.passwordIconTypeConfirm = "eye-off";
    } else {
      this.passwordInputTypeConfirm = "password";
      this.passwordIconTypeConfirm = "eye";
    }
  }

  /**
   * This method create an array containing  "UsersRole"
   * This array is used to dinamically initialise the "ion-select-option"-elements list in the template.
   */
  private async initialiseArrayRole(): Promise<void> {
    this.arrayRole.push(UsersRole.BUILDING_ADMINISTRATOR);
    this.arrayRole.push(UsersRole.MAINTANER);
    this.arrayRole.push(UsersRole.RESIDENT);
  }

  private subscribeToCurrentUser() {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((user: CurrentSessionData) => {
        this._userStore = user;
        this.formCreateAccount
          .get("email")
          .setValue(this._securetyDataProtectionService.decypt(user.username));

        this.formCreateAccount.get("name").setValue(this._userStore.name);
        this.formCreateAccount.get("surname").setValue(this._userStore.surname);
      });
  }

  /**
   * Initialize login form plant
   */
  private initializeCreateAccountForm(): void {
    this.formCreateAccount = this._fb.group({
      email: new UntypedFormControl({ value: "", disabled: true }),
      name: new UntypedFormControl("", Validators.required),
      surname: new UntypedFormControl("", Validators.required),
      role: new UntypedFormControl(UsersRole.BUILDING_ADMINISTRATOR, [
        Validators.required,
      ]),
      acceptRegisterCondition: new UntypedFormControl(false, Validators.requiredTrue),
    });
  }

  /**
   * Subscribe value change event from each form field.
   */
  private subscribeFormEvent(): void {
    this._formSubscriptions = new Array<Subscription>();
    this.subscribeToCurrentUser();
  }

  /**
   * Enter pressed in a field: same as the save button, when name and surname are filled in
   * (the whole form is never valid here: "acceptRegisterCondition" is not shown on this page)
   */
  public async onSubmitForm(): Promise<void> {
    if (this.formCreateAccount.get("name").valid && this.formCreateAccount.get("surname").valid) {
      await this.onClickConfirm();
    }
  }

  /**
   * Perform create account procedure.
   *
   * @returns
   */
  public async onClickConfirm(): Promise<void> {
    try {
      let accountNew: AccountUpdate = this.formCreateAccount.value;
      accountNew.name = this.formCreateAccount.get("name").value;
      accountNew.surname = this.formCreateAccount.get("surname").value;

      await this._accountService.performDPEditAccount(accountNew);

      this._store.dispatch(
        currentUserUpdateData({
          name: accountNew.name,
          surname: accountNew.surname,
        })
      );

      this.onSaveData.emit();
    } catch (exception) {
      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Unsubscribe to form event events
   */
  private unsubscribeFormEvent(): void {
    for (const sub of this._formSubscriptions) {
      sub.unsubscribe();
    }
  }
}
